import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MapView } from '@/components/Map';
import { toast } from 'sonner';
import { Search, Plus, GripVertical, X, Move, Eye, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { validCoords, stopRpcErrorMessage } from '@/lib/corridorUtils';

interface TransitStop {
  id: string;
  name: string;
  display_name: string | null;
  lat: number;
  lng: number;
  aliases: string[] | null;
  notes: string | null;
  is_active: boolean;
  is_draft: boolean;
  is_hub?: boolean;
  hub_radius_m?: number | null;
  corridor_count?: number;
  corridors?: { id: string; name: string }[];
}

interface StopsMapProps {
  stops: TransitStop[];
  corridors: { id: string; name: string }[];
  onRefresh: () => void;
  onNavigateToCorridor?: (corridorId: string) => void;
}

type FilterMode = 'all' | 'published' | 'draft' | 'missing';

export default function StopsMap({ stops, corridors, onRefresh, onNavigateToCorridor }: StopsMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [mode, setMode] = useState<'inspect' | 'drag'>('inspect');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [search, setSearch] = useState('');
  const [selectedStop, setSelectedStop] = useState<TransitStop | null>(null);
  const [editForm, setEditForm] = useState<{
    display_name: string;
    aliases: string[];
    notes: string;
    newAlias: string;
    is_hub: boolean;
    hub_radius_m: string;
  }>({ display_name: '', aliases: [], notes: '', newAlias: '', is_hub: false, hub_radius_m: '' });
  const [saving, setSaving] = useState(false);
  const [addWizardOpen, setAddWizardOpen] = useState(false);
  const [dragConfirm, setDragConfirm] = useState<{
    stop: TransitStop;
    newLat: number;
    newLng: number;
    distance: number;
  } | null>(null);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Add wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardForm, setWizardForm] = useState({
    name: '',
    display_name: '',
    lat: 0,
    lng: 0,
    aliases: [] as string[],
    notes: '',
    newAlias: '',
    is_hub: false,
    hub_radius_m: '',
  });
  const [wizardSaving, setWizardSaving] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const wizardMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  // Filter stops
  const filteredStops = stops.filter(s => {
    if (filter === 'published') return !s.is_draft;
    if (filter === 'draft') return s.is_draft;
    if (filter === 'missing') return s.notes?.startsWith('SEED: missing coordinates');
    return true;
  }).filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) ||
      (s.display_name || '').toLowerCase().includes(q) ||
      (s.aliases || []).some(a => a.toLowerCase().includes(q));
  });

  const draftCount = stops.filter(s => s.is_draft).length;

  // Get pin color
  const getPinColor = (stop: TransitStop): string => {
    if (stop.is_draft) return '#3b82f6'; // blue
    if (stop.notes?.startsWith('SEED: missing coordinates')) return '#f59e0b'; // amber
    if (stop.is_hub) return '#a855f7'; // violet — hub / bus stand
    return '#22c55e'; // green
  };

  // Render markers
  const renderMarkers = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    // Clear existing markers
    markersRef.current.forEach(m => { m.map = null; });
    markersRef.current = [];

    // Use validCoords to defensively filter out stops with missing/invalid coordinates
    const drawableStops = validCoords(filteredStops);
    drawableStops.forEach(stop => {

      const color = getPinColor(stop);
      const pinEl = document.createElement('div');
      pinEl.style.cssText = `
        width: 14px; height: 14px; border-radius: 50%;
        background: ${color}; border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        cursor: ${mode === 'drag' ? 'grab' : 'pointer'};
        transition: transform 0.15s ease;
      `;
      pinEl.title = stop.name;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current!,
        position: { lat: stop.lat, lng: stop.lng },
        content: pinEl,
        title: stop.name,
        gmpDraggable: mode === 'drag',
      });

      // Click handler
      marker.addListener('click', () => {
        setSelectedStop(stop);
        setEditForm({
          display_name: stop.display_name || '',
          aliases: stop.aliases || [],
          notes: stop.notes || '',
          newAlias: '',
          is_hub: !!stop.is_hub,
          hub_radius_m: stop.hub_radius_m != null ? String(stop.hub_radius_m) : '',
        });
      });

      // Drag end handler
      if (mode === 'drag') {
        marker.addListener('dragend', () => {
          const pos = marker.position as google.maps.LatLngLiteral;
          if (!pos) return;
          const distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(stop.lat, stop.lng),
            new google.maps.LatLng(pos.lat, pos.lng)
          );
          setDragConfirm({
            stop,
            newLat: pos.lat,
            newLng: pos.lng,
            distance: Math.round(distance),
          });
        });
      }

      markersRef.current.push(marker);
    });
  }, [filteredStops, mode]);

  useEffect(() => {
    renderMarkers();
  }, [renderMarkers]);

  // Handle search select — center map on stop
  const handleSearchSelect = (stop: TransitStop) => {
    if (mapRef.current && stop.lat && stop.lng) {
      mapRef.current.panTo({ lat: stop.lat, lng: stop.lng });
      mapRef.current.setZoom(15);
      setSelectedStop(stop);
      setEditForm({
        display_name: stop.display_name || '',
        aliases: stop.aliases || [],
        notes: stop.notes || '',
        newAlias: '',
        is_hub: !!stop.is_hub,
        hub_radius_m: stop.hub_radius_m != null ? String(stop.hub_radius_m) : '',
      });
    }
  };

  // Save stop edits
  const handleSaveStop = async (isDraft: boolean) => {
    if (!selectedStop) return;
    setSaving(true);
    try {
      const { data: saveData, error } = await supabase.rpc('admin_upsert_stop', {
        p_id: selectedStop.id,
        p_name: selectedStop.name,
        p_display_name: editForm.display_name || null,
        p_lat: selectedStop.lat,
        p_lng: selectedStop.lng,
        p_aliases: editForm.aliases.length > 0 ? editForm.aliases : null,
        p_notes: editForm.notes || null,
        p_is_draft: isDraft,
        p_is_hub: editForm.is_hub,
        p_hub_radius_m:
          editForm.is_hub && editForm.hub_radius_m.trim()
            ? Number(editForm.hub_radius_m)
            : null,
      });
      if (error) throw error;
      const saveResult = saveData as any;
      if (saveResult?.ok === false) throw new Error(stopRpcErrorMessage(saveResult.error));
      toast.success(isDraft ? 'Stop saved as draft' : 'Stop saved and published');
      setSelectedStop(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save stop');
    } finally {
      setSaving(false);
    }
  };

  // Confirm drag move
  const handleConfirmDrag = async () => {
    if (!dragConfirm) return;
    setSaving(true);
    try {
      const { data: dragData, error } = await supabase.rpc('admin_upsert_stop', {
        p_id: dragConfirm.stop.id,
        p_name: dragConfirm.stop.name,
        p_display_name: dragConfirm.stop.display_name || null,
        p_lat: dragConfirm.newLat,
        p_lng: dragConfirm.newLng,
        p_aliases: dragConfirm.stop.aliases || null,
        p_notes: dragConfirm.stop.notes || null,
        p_is_draft: true,
        p_is_hub: !!dragConfirm.stop.is_hub,
        p_hub_radius_m: dragConfirm.stop.hub_radius_m ?? null,
      });
      if (error) throw error;
      const dragResult = dragData as any;
      if (dragResult?.ok === false) throw new Error(stopRpcErrorMessage(dragResult.error));
      toast.success(`'${dragConfirm.stop.name}' moved and saved as draft`);
      setDragConfirm(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to move stop');
      setDragConfirm(null);
      renderMarkers(); // Reset marker position
    } finally {
      setSaving(false);
    }
  };

  // Publish all drafts
  const handlePublishAll = async () => {
    setPublishing(true);
    try {
      const { data: pubData, error } = await supabase.rpc('admin_publish_drafts', { p_corridor_id: null });
      if (error) throw error;
      const pubResult = pubData as any;
      if (pubResult?.ok === false) throw new Error(pubResult.error || 'Publish failed');
      toast.success('All drafts published. Mobile apps will sync within seconds.');
      setPublishConfirm(false);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish drafts');
    } finally {
      setPublishing(false);
    }
  };

  // Discard all drafts
  const handleDiscardAll = async () => {
    try {
      const { data: discardData, error } = await supabase.rpc('admin_discard_drafts', { p_corridor_id: null });
      if (error) throw error;
      const discardResult = discardData as any;
      if (discardResult?.ok === false) throw new Error(discardResult.error || 'Discard failed');
      toast.success('All drafts discarded');
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to discard drafts');
    }
  };

  // Add stop wizard — save
  const handleWizardSave = async () => {
    if (!wizardForm.name.trim()) {
      toast.error('Stop name is required');
      return;
    }
    setWizardSaving(true);
    try {
      const { data: addData, error } = await supabase.rpc('admin_upsert_stop', {
        p_id: null,
        p_name: wizardForm.name.trim(),
        p_display_name: wizardForm.display_name.trim() || null,
        p_lat: wizardForm.lat,
        p_lng: wizardForm.lng,
        p_aliases: wizardForm.aliases.length > 0 ? wizardForm.aliases : null,
        p_notes: wizardForm.notes.trim() || null,
        p_is_draft: true,
        p_is_hub: wizardForm.is_hub,
        p_hub_radius_m:
          wizardForm.is_hub && wizardForm.hub_radius_m.trim()
            ? Number(wizardForm.hub_radius_m)
            : null,
      });
      if (error) throw error;
      const addResult = addData as any;
      if (addResult?.ok === false) throw new Error(stopRpcErrorMessage(addResult.error));
      toast.success(`'${wizardForm.name}' added as draft`);
      setAddWizardOpen(false);
      setWizardStep(1);
      setWizardForm({ name: '', display_name: '', lat: 0, lng: 0, aliases: [], notes: '', newAlias: '', is_hub: false, hub_radius_m: '' });
      if (wizardMarkerRef.current) {
        wizardMarkerRef.current.map = null;
        wizardMarkerRef.current = null;
      }
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add stop');
    } finally {
      setWizardSaving(false);
    }
  };

  // Initialize Places Autocomplete for wizard
  const initAutocomplete = useCallback((inputEl: HTMLInputElement | null) => {
    if (!inputEl || !window.google?.maps?.places) return;
    if (autocompleteRef.current) return;
    autocompleteRef.current = new google.maps.places.Autocomplete(inputEl, {
      componentRestrictions: { country: 'dm' },
      fields: ['geometry', 'name'],
    });
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current!.getPlace();
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setWizardForm(prev => ({
          ...prev,
          name: prev.name || place.name || '',
          lat,
          lng,
        }));
        // Move map and drop marker
        if (mapRef.current) {
          mapRef.current.panTo({ lat, lng });
          mapRef.current.setZoom(15);
          if (wizardMarkerRef.current) {
            wizardMarkerRef.current.position = { lat, lng };
          } else {
            const pinEl = document.createElement('div');
            pinEl.style.cssText = `
              width: 20px; height: 20px; border-radius: 50%;
              background: #3b82f6; border: 3px solid white;
              box-shadow: 0 2px 8px rgba(59,130,246,0.5);
              opacity: 0.8; cursor: grab;
            `;
            wizardMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
              map: mapRef.current,
              position: { lat, lng },
              content: pinEl,
              gmpDraggable: true,
            });
            wizardMarkerRef.current.addListener('dragend', () => {
              const pos = wizardMarkerRef.current!.position as google.maps.LatLngLiteral;
              if (pos) {
                setWizardForm(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }));
              }
            });
          }
        }
        setWizardStep(2);
      }
    });
  }, []);

  return (
    <div className="relative h-full flex">
      {/* Map area */}
      <div className="flex-1 relative">
        {/* Toolbar */}
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search stops..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-gray-900/90 border-gray-700 text-white backdrop-blur-sm h-9"
            />
            {/* Search dropdown */}
            {search && filteredStops.length > 0 && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-gray-900 border border-gray-700 rounded-md shadow-xl max-h-48 overflow-y-auto z-50">
                {filteredStops.slice(0, 8).map(s => (
                  <button
                    key={s.id}
                    onClick={() => { handleSearchSelect(s); setSearch(''); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: getPinColor(s) }} />
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Select value={filter} onValueChange={(v) => setFilter(v as FilterMode)}>
            <SelectTrigger className="w-[140px] bg-gray-900/90 border-gray-700 text-white backdrop-blur-sm h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stops</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
              <SelectItem value="missing">Missing coords</SelectItem>
            </SelectContent>
          </Select>

          {/* Mode toggle */}
          <div className="flex bg-gray-900/90 border border-gray-700 rounded-md backdrop-blur-sm">
            <button
              onClick={() => setMode('inspect')}
              className={`px-3 py-1.5 text-xs font-medium rounded-l-md flex items-center gap-1.5 transition-colors ${
                mode === 'inspect' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Eye className="w-3.5 h-3.5" /> Inspect
            </button>
            <button
              onClick={() => setMode('drag')}
              className={`px-3 py-1.5 text-xs font-medium rounded-r-md flex items-center gap-1.5 transition-colors ${
                mode === 'drag' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Move className="w-3.5 h-3.5" /> Drag
            </button>
          </div>

          <Button
            size="sm"
            onClick={() => { setAddWizardOpen(true); setWizardStep(1); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
          >
            <Plus className="w-4 h-4 mr-1" /> Add stop
          </Button>

          <div className="flex gap-2 ml-auto">
            {draftCount > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDiscardAll}
                  className="border-gray-600 text-gray-300 hover:text-white h-9"
                >
                  Discard drafts
                </Button>
                <Button
                  size="sm"
                  onClick={() => setPublishConfirm(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white h-9"
                >
                  Publish all drafts ({draftCount})
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-4 left-4 z-10 flex gap-2">
          <Badge variant="outline" className="bg-gray-900/90 border-gray-700 text-gray-300 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5" />
            {stops.filter(s => !s.is_draft && !s.notes?.startsWith('SEED:')).length} published
          </Badge>
          <Badge variant="outline" className="bg-gray-900/90 border-gray-700 text-gray-300 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5" />
            {stops.filter(s => s.notes?.startsWith('SEED: missing coordinates')).length} missing coords
          </Badge>
          <Badge variant="outline" className="bg-gray-900/90 border-gray-700 text-gray-300 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-blue-500 mr-1.5" />
            {draftCount} drafts
          </Badge>
        </div>

        <MapView
          className="w-full h-full"
          initialCenter={{ lat: 15.41, lng: -61.37 }}
          initialZoom={11}
          onMapReady={(map) => {
            mapRef.current = map;
            // Dark style for admin
            map.setOptions({
              styles: [
                { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
                { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
                { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
                { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#0e1626' }] },
                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
                { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
              ],
            });
          }}
        />
      </div>

      {/* Side panel — stop detail */}
      {selectedStop && (
        <div className="w-80 bg-gray-900 border-l border-gray-800 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">{selectedStop.name}</h3>
            <button onClick={() => setSelectedStop(null)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {selectedStop.is_draft && (
            <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">Draft</Badge>
          )}
          {selectedStop.notes?.startsWith('SEED:') && (
            <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30">Missing coords</Badge>
          )}
          {editForm.is_hub && (
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-600/30">Hub stand</Badge>
          )}

          <div className="space-y-3">
            <div>
              <Label className="text-gray-400 text-xs">Display Name</Label>
              <Input
                value={editForm.display_name}
                onChange={e => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="Optional display name"
                className="bg-gray-800 border-gray-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-gray-400 text-xs">Coordinates</Label>
              <p className="text-sm text-gray-300 font-mono mt-1">
                {selectedStop.lat.toFixed(6)}, {selectedStop.lng.toFixed(6)}
              </p>
              {mode === 'inspect' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setMode('drag')}
                  className="text-blue-400 hover:text-blue-300 mt-1 h-7 text-xs px-2"
                >
                  <Move className="w-3 h-3 mr-1" /> Drag to correct
                </Button>
              )}
            </div>

            <div>
              <Label className="text-gray-400 text-xs">Aliases</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {editForm.aliases.map((alias, i) => (
                  <Badge key={i} variant="secondary" className="bg-gray-800 text-gray-300 text-xs">
                    {alias}
                    <button
                      onClick={() => setEditForm(prev => ({
                        ...prev,
                        aliases: prev.aliases.filter((_, idx) => idx !== i)
                      }))}
                      className="ml-1 text-gray-500 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-1 mt-1">
                <Input
                  value={editForm.newAlias}
                  onChange={e => setEditForm(prev => ({ ...prev, newAlias: e.target.value }))}
                  placeholder="Add alias..."
                  className="bg-gray-800 border-gray-700 text-white text-xs h-7"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editForm.newAlias.trim()) {
                      setEditForm(prev => ({
                        ...prev,
                        aliases: [...prev.aliases, prev.newAlias.trim()],
                        newAlias: '',
                      }));
                    }
                  }}
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-400 text-xs">Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notes..."
                className="bg-gray-800 border-gray-700 text-white mt-1 text-sm min-h-[60px]"
              />
            </div>

            {/* Hub stand (terminus fill-and-go) */}
            <div className="rounded-md border border-gray-800 bg-gray-800/40 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-gray-200 text-sm">Hub / bus stand</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Buses queue &amp; fill here before departing.
                  </p>
                </div>
                <Switch
                  checked={editForm.is_hub}
                  onCheckedChange={(v) => setEditForm(prev => ({ ...prev, is_hub: v }))}
                />
              </div>
              {editForm.is_hub && (
                <div>
                  <Label className="text-gray-400 text-xs">Geofence radius (m)</Label>
                  <Input
                    type="number"
                    min="20"
                    step="10"
                    value={editForm.hub_radius_m}
                    onChange={e => setEditForm(prev => ({ ...prev, hub_radius_m: e.target.value }))}
                    placeholder="Default 200"
                    className="bg-gray-800 border-gray-700 text-white mt-1 text-sm h-8"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    How close a bus must be to count as "at the stand". Leave blank for 200m.
                  </p>
                </div>
              )}
            </div>

            {/* Corridor usage */}
            {selectedStop.corridors && selectedStop.corridors.length > 0 && (
              <div>
                <Label className="text-gray-400 text-xs">
                  Used in {selectedStop.corridors.length} corridor{selectedStop.corridors.length > 1 ? 's' : ''}
                </Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedStop.corridors.map(c => (
                    <button
                      key={c.id}
                      onClick={() => onNavigateToCorridor?.(c.id)}
                      className="text-xs px-2 py-0.5 rounded bg-gray-800 text-emerald-400 hover:bg-gray-700 transition-colors"
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-800">
            <Button
              size="sm"
              onClick={() => handleSaveStop(true)}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save as draft
            </Button>
            <Button
              size="sm"
              onClick={() => handleSaveStop(false)}
              disabled={saving}
              variant="outline"
              className="flex-1 border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
            >
              Save & publish
            </Button>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedStop(null)}
            className="w-full text-gray-400 hover:text-white"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Drag confirm modal */}
      <Dialog open={!!dragConfirm} onOpenChange={() => { setDragConfirm(null); renderMarkers(); }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Confirm stop relocation</DialogTitle>
            <DialogDescription className="text-gray-400">
              Move '{dragConfirm?.stop.name}' from ({dragConfirm?.stop.lat.toFixed(4)}, {dragConfirm?.stop.lng.toFixed(4)}) to ({dragConfirm?.newLat.toFixed(4)}, {dragConfirm?.newLng.toFixed(4)})?
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-300">
            Distance: <span className="font-mono text-amber-400">{dragConfirm?.distance}m</span>
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDragConfirm(null); renderMarkers(); }}>Cancel</Button>
            <Button onClick={handleConfirmDrag} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Confirm move (draft)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish confirm modal */}
      <Dialog open={publishConfirm} onOpenChange={setPublishConfirm}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Publish all drafts?</DialogTitle>
            <DialogDescription className="text-gray-400">
              This will publish {draftCount} draft stop{draftCount > 1 ? 's' : ''}. Mobile apps will see the changes within seconds.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {stops.filter(s => s.is_draft).map(s => (
              <div key={s.id} className="text-sm text-gray-300 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                {s.name}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishConfirm(false)}>Cancel</Button>
            <Button onClick={handlePublishAll} disabled={publishing} className="bg-emerald-600 hover:bg-emerald-700">
              {publishing && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Publish all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add stop wizard */}
      <Dialog open={addWizardOpen} onOpenChange={(open) => {
        setAddWizardOpen(open);
        if (!open && wizardMarkerRef.current) {
          wizardMarkerRef.current.map = null;
          wizardMarkerRef.current = null;
        }
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add new stop</DialogTitle>
            <DialogDescription className="text-gray-400">
              {wizardStep === 1 ? 'Search for a place in Dominica to set the initial location.' : 'Refine the location and fill in details.'}
            </DialogDescription>
          </DialogHeader>

          {wizardStep === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="text-gray-400 text-xs">Search place in Dominica</Label>
                <Input
                  ref={initAutocomplete}
                  placeholder="Type a place name..."
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                />
              </div>
              <p className="text-xs text-gray-500">
                Or manually enter coordinates below and skip to step 2.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-gray-400 text-xs">Latitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={wizardForm.lat || ''}
                    onChange={e => setWizardForm(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Longitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={wizardForm.lng || ''}
                    onChange={e => setWizardForm(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                  />
                </div>
              </div>
              <Button
                onClick={() => setWizardStep(2)}
                disabled={!wizardForm.lat && !wizardForm.lng}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Next
              </Button>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-3">
              <div>
                <Label className="text-gray-400 text-xs">Stop Name (canonical)</Label>
                <Input
                  value={wizardForm.name}
                  onChange={e => setWizardForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Wesley"
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Display Name (optional)</Label>
                <Input
                  value={wizardForm.display_name}
                  onChange={e => setWizardForm(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="e.g. Wesley Junction"
                  className="bg-gray-800 border-gray-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">
                  Location: {wizardForm.lat.toFixed(4)}, {wizardForm.lng.toFixed(4)}
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">Drag the blue pin on the map to refine.</p>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Aliases</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {wizardForm.aliases.map((a, i) => (
                    <Badge key={i} variant="secondary" className="bg-gray-800 text-gray-300 text-xs">
                      {a}
                      <button onClick={() => setWizardForm(prev => ({
                        ...prev, aliases: prev.aliases.filter((_, idx) => idx !== i)
                      }))} className="ml-1 text-gray-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <Input
                  value={wizardForm.newAlias}
                  onChange={e => setWizardForm(prev => ({ ...prev, newAlias: e.target.value }))}
                  placeholder="Type alias + Enter"
                  className="bg-gray-800 border-gray-700 text-white text-xs h-7 mt-1"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && wizardForm.newAlias.trim()) {
                      setWizardForm(prev => ({
                        ...prev,
                        aliases: [...prev.aliases, prev.newAlias.trim()],
                        newAlias: '',
                      }));
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Notes</Label>
                <Textarea
                  value={wizardForm.notes}
                  onChange={e => setWizardForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-gray-800 border-gray-700 text-white mt-1 text-sm min-h-[50px]"
                />
              </div>
              <div className="rounded-md border border-gray-800 bg-gray-800/40 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-200 text-sm">Hub / bus stand</Label>
                  <Switch
                    checked={wizardForm.is_hub}
                    onCheckedChange={(v) => setWizardForm(prev => ({ ...prev, is_hub: v }))}
                  />
                </div>
                {wizardForm.is_hub && (
                  <Input
                    type="number"
                    min="20"
                    step="10"
                    value={wizardForm.hub_radius_m}
                    onChange={e => setWizardForm(prev => ({ ...prev, hub_radius_m: e.target.value }))}
                    placeholder="Geofence radius (m), default 200"
                    className="bg-gray-800 border-gray-700 text-white text-sm h-8"
                  />
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setWizardStep(1)} className="flex-1">Back</Button>
                <Button
                  onClick={handleWizardSave}
                  disabled={wizardSaving || !wizardForm.name.trim()}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {wizardSaving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Save as draft
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
